// ============================================================================
// ATTACHMENTS STEP PHI — GP attachment staging UI
//
// This component manages LOCAL state only — it does NOT call any Cloud
// Functions or write to Firestore/Storage. File objects are staged in memory
// and uploaded by the parent (GateView) after submitReferral() succeeds and
// a referralId is available.
//
// Attachment types match phiAttachmentsApi.js:
//   LABS | IMAGING | PDF | PHOTO | OTHER
//
// Status lifecycle (driven by parent props):
//   staged → uploading → ready | failed
//
// PHI Architecture: real uploads happen via CFs (createAttachmentUploadUrl +
//   browser PUT to signed URL + finalizeAttachmentUpload). No direct Storage
//   or Firestore access from the client.
// ============================================================================

import React, { useRef, useState } from 'react';
import {
  Paperclip, X, Loader2, CheckCircle2, AlertCircle, Upload, FileText,
} from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────────────────────

const ATTACHMENT_TYPES = [
  { value: 'LABS',    label: 'Lab Results' },
  { value: 'IMAGING', label: 'Imaging Report' },
  { value: 'PDF',     label: 'PDF Document' },
  { value: 'PHOTO',   label: 'Photo / Scan' },
  { value: 'OTHER',   label: 'Other' },
];

const STATUS_CONFIG = {
  staged:    { label: 'Ready to upload',  cls: 'bg-slate-600/50 text-slate-300 border-slate-600/50' },
  uploading: { label: 'Uploading…',       cls: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  ready:     { label: 'Uploaded',         cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  failed:    { label: 'Upload failed',    cls: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

const ACCEPTED = '.pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.tif,.tiff,image/*,application/pdf';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SelectChevron = () => (
  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
    <svg className="h-4 w-4 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
  </div>
);

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {Array<{id,file,type,originalName,status,sizeBytes?}>} props.files
 *   Controlled list of staged / in-flight / completed attachment entries.
 * @param {(fileList: File[], type: string) => void} props.onAddFiles
 *   Called when the user picks files. Parent appends them as 'staged'.
 * @param {(id: string) => void} props.onRemoveFile
 *   Called when the user clicks ✕ on a staged or failed entry.
 * @param {boolean} [props.disabled]
 *   True while the parent's submit is in progress.
 */
const AttachmentsStepPHI = ({ files = [], onAddFiles, onRemoveFile, disabled = false }) => {
  const fileInputRef = useRef(null);
  const [selectedType, setSelectedType] = useState('LABS');

  const handleFileChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    onAddFiles(picked, selectedType);
    // Reset so the same file can be re-selected after a failed upload
    e.target.value = '';
  };

  const readyCount   = files.filter(f => f.status === 'ready').length;
  const failedCount  = files.filter(f => f.status === 'failed').length;

  return (
    <div className="space-y-4">

      {/* ── Picker row ── */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Type selector */}
        <div className="relative flex-shrink-0">
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            disabled={disabled}
            className="bg-slate-800/50 border border-slate-700/50 rounded-lg pl-3 pr-7 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/50 appearance-none cursor-pointer disabled:opacity-50"
          >
            {ATTACHMENT_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <SelectChevron />
        </div>

        {/* Attach button */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm text-white transition-colors"
        >
          <Paperclip className="w-4 h-4" />
          Attach File
        </button>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED}
          onChange={handleFileChange}
          className="hidden"
        />

        <span className="text-xs text-slate-500">PDF, JPEG, PNG · Optional</span>
      </div>

      {/* ── File list ── */}
      {files.length > 0 ? (
        <div className="space-y-2">
          {files.map(entry => {
            const cfg       = STATUS_CONFIG[entry.status] || STATUS_CONFIG.staged;
            const typeLabel = ATTACHMENT_TYPES.find(t => t.value === entry.type)?.label || entry.type;
            const size      = entry.sizeBytes != null
              ? formatBytes(entry.sizeBytes)
              : entry.file?.size != null
                ? formatBytes(entry.file.size)
                : null;

            return (
              <div
                key={entry.id}
                className="flex items-center gap-3 px-3 py-2.5 bg-slate-800/40 border border-slate-700/40 rounded-lg"
              >
                {/* File icon */}
                <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />

                {/* Name + meta */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{entry.originalName}</p>
                  <p className="text-xs text-slate-500">
                    {typeLabel}{size ? ` · ${size}` : ''}
                  </p>
                </div>

                {/* Status chip */}
                <span className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs border font-medium ${cfg.cls}`}>
                  {entry.status === 'uploading' && <Loader2 className="w-3 h-3 animate-spin" />}
                  {entry.status === 'ready'     && <CheckCircle2 className="w-3 h-3" />}
                  {entry.status === 'failed'    && <AlertCircle  className="w-3 h-3" />}
                  {cfg.label}
                </span>

                {/* Remove — only for staged or failed */}
                {(entry.status === 'staged' || entry.status === 'failed') && (
                  <button
                    type="button"
                    onClick={() => onRemoveFile(entry.id)}
                    disabled={disabled}
                    title="Remove"
                    className="flex-shrink-0 p-1 text-slate-500 hover:text-red-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}

          {/* Summary line after uploads complete */}
          {(readyCount > 0 || failedCount > 0) && files.every(f => f.status !== 'uploading') && (
            <p className="text-xs text-slate-500 pl-1">
              {readyCount > 0 && `${readyCount} file${readyCount > 1 ? 's' : ''} uploaded`}
              {readyCount > 0 && failedCount > 0 && ' · '}
              {failedCount > 0 && (
                <span className="text-red-400">
                  {failedCount} failed — referral still submitted
                </span>
              )}
            </p>
          )}
        </div>
      ) : (
        /* Empty state */
        <div className="flex items-center gap-3 px-4 py-3 bg-slate-800/20 border border-dashed border-slate-700/50 rounded-lg">
          <Upload className="w-4 h-4 text-slate-600 flex-shrink-0" />
          <p className="text-sm text-slate-500">
            No attachments added. You can submit without attachments.
          </p>
        </div>
      )}

    </div>
  );
};

export default AttachmentsStepPHI;
